import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import FAQSection from '@/components/base/FAQSection';
import { faqs } from '@/mocks/faqs';

const testimonials = [
  {
    name: '张先生',
    role: '社区资深用户',
    content: '自从收藏了这个导航站，再也不用担心找不到入口了。更新及时，推荐给身边的朋友都在用。',
  },
  {
    name: '李女士',
    role: '新用户',
    content: '作为新手的我，通过这里的访问指南很快就学会了如何稳定访问社区，教程非常详细易懂。',
  },
  {
    name: '王同学',
    role: '大学生用户',
    content: '镜像站点列表太实用了，校园网环境下也能快速找到可用的入口，感谢维护团队！',
  },
  {
    name: '赵先生',
    role: '技术爱好者',
    content: 'DNS优化和安全访问建议特别专业，按照教程配置后访问速度提升明显，强烈推荐。',
  },
  {
    name: '刘女士',
    role: '长期用户',
    content: '用了快两年的导航站了，入口更新从未断过。团队的专业和用心真的能感受到，必须给好评。',
  },
  {
    name: '陈先生',
    role: '海外用户',
    content: '人在国外，通过这里的全球镜像列表找到了最快的节点，访问体验比之前好太多了。',
  },
];

const aboutFaqs = faqs;

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q6kpsnlsngrm4llsf0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormMessage(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <SchemaOrg
        type="FAQPage"
        data={{
          name: '关于我们 - 草榴社区入口导航',
          description: '了解草榴社区入口导航的品牌故事、口碑评价与联系方式',
          mainEntity: aboutFaqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        }}
      />

      <SchemaOrg
        type="Organization"
        data={{
          name: '草榴社区入口导航',
          description: '最全面的草榴社区入口导航平台，提供最新可用入口地址、镜像站点及访问指南。',
          contactPoint: {
            '@type': 'ContactPoint',
            email: 'contact@caoliu-nav.com',
            contactType: 'customer service',
            availableLanguage: ['Chinese'],
          },
        }}
      />

      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                关于我们
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                草榴社区入口导航是专注于为华语用户提供草榴社区访问解决方案的专业导航平台。
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg mb-4">
                  <i className="ri-flashlight-line text-xl"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">我们的使命</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  在网络环境日益复杂的今天，我们致力于为每一位用户提供稳定可靠的草榴社区入口导航服务。
                  通过持续监控入口可用性、整理镜像站点信息、编写详细的访问指南，我们希望帮助每一位用户告别"找不到入口"的烦恼。
                </p>
              </div>

              <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
                <div className="w-12 h-12 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg mb-4">
                  <i className="ri-eye-line text-xl"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">我们的愿景</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  成为华语地区最权威的草榴社区入口导航平台。我们相信信息不应该被封锁阻隔，
                  每一位用户都应该拥有自由、稳定的访问体验。我们将不断优化服务，扩展覆盖范围。
                </p>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3 text-center">我们的优势</h2>
              <p className="text-sm text-foreground-500 text-center mb-8">为什么选择我们作为您的入口导航伙伴</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: 'ri-timer-flash-line', title: '实时更新', desc: '入口状态持续监控，更新频率达到每日级别' },
                  { icon: 'ri-shield-check-line', title: '安全可靠', desc: '所有入口经过人工验证，确保安全可用' },
                  { icon: 'ri-global-line', title: '全球覆盖', desc: '汇总全球多个地区的镜像站点信息' },
                  { icon: 'ri-book-open-line', title: '教程详尽', desc: '从入门到进阶，覆盖各类访问场景' },
                ].map((item) => (
                  <div key={item.title} className="bg-background-50 rounded-lg border border-background-200/70 p-5 text-center">
                    <div className="w-10 h-10 mx-auto flex items-center justify-center bg-secondary-100 text-secondary-600 rounded-lg mb-3">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-2">{item.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3 text-center">用户口碑</h2>
              <p className="text-sm text-foreground-500 text-center mb-8">听听用户怎么说</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {testimonials.map((t) => (
                  <div key={t.name} className="bg-background-50 rounded-lg border border-background-200/70 p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 text-xs font-bold">
                        {t.name.charAt(0)}
                      </div>
                      <div>
                        <span className="text-sm font-medium text-foreground-800 block">{t.name}</span>
                        <span className="text-xs text-foreground-400">{t.role}</span>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">&ldquo;{t.content}&rdquo;</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3 text-center">联系我们</h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              有任何问题或建议？欢迎给我们留言
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="flex flex-col gap-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-500">contact@caoliu-nav.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">更新时间</h3>
                    <p className="text-sm text-foreground-500">每日检查并更新入口状态</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-secondary-100 text-secondary-600 rounded-lg">
                    <i className="ri-customer-service-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">服务时间</h3>
                    <p className="text-sm text-foreground-500">7×24小时自动监控，工作日回复留言</p>
                  </div>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-50 rounded-lg border border-background-200/70 p-6 flex flex-col gap-4"
              >
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入留言主题"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                {formStatus !== 'idle' && (
                  <div className={`text-sm p-3 rounded-md ${
                    formStatus === 'success'
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {formMessage}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full py-2.5 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  提交留言
                </button>
              </form>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3 text-center">常见问题</h2>
            <p className="text-sm text-foreground-500 text-center mb-8">关于我们与服务的常见问题</p>
            <FAQSection faqs={aboutFaqs} />
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}